<template>
  <div class="page-container">
    <a-card title="后处理屏蔽判断日志">
      <!-- 筛选栏 -->
      <a-form layout="inline" :model="filters" style="margin-bottom: 16px">
        <a-form-item label="业务标识">
          <a-input
            v-model:value="filters.system_id"
            placeholder="system_id 关键词"
            allow-clear
            style="width: 160px"
            @pressEnter="handleSearch"
          />
        </a-form-item>
        <a-form-item label="车牌号">
          <a-input
            v-model:value="filters.license_plate"
            placeholder="车牌号关键词"
            allow-clear
            style="width: 160px"
            @pressEnter="handleSearch"
          />
        </a-form-item>
        <a-form-item label="提示词版本">
          <a-input-number
            v-model:value="filters.prompt_version"
            :min="1"
            :precision="0"
            placeholder="版本号"
            allow-clear
            style="width: 110px"
            @pressEnter="handleSearch"
          />
        </a-form-item>
        <a-form-item label="排放阶段">
          <a-select
            v-model:value="filters.emission_level"
            placeholder="全部"
            allow-clear
            style="width: 110px"
          >
            <a-select-option value="国三">国三</a-select-option>
            <a-select-option value="国四">国四</a-select-option>
            <a-select-option value="国五">国五</a-select-option>
            <a-select-option value="国六">国六</a-select-option>
            <a-select-option value="不明确">不明确</a-select-option>
          </a-select>
        </a-form-item>
        <a-form-item label="污染物装置">
          <a-select
            v-model:value="filters.pollutant_type"
            placeholder="全部"
            allow-clear
            style="width: 120px"
          >
            <a-select-option value="无">无</a-select-option>
            <a-select-option value="SCR">SCR</a-select-option>
            <a-select-option value="DPF">DPF</a-select-option>
            <a-select-option value="SCR+DPF">SCR+DPF</a-select-option>
          </a-select>
        </a-form-item>
        <a-form-item label="状态">
          <a-select
            v-model:value="filters.status"
            placeholder="全部"
            allow-clear
            style="width: 90px"
          >
            <a-select-option value="1">成功</a-select-option>
            <a-select-option value="0">失败</a-select-option>
          </a-select>
        </a-form-item>
        <a-form-item label="时间范围">
          <a-range-picker
            v-model:value="filters.timeRange"
            show-time
            format="YYYY-MM-DD HH:mm"
            style="width: 350px"
          />
        </a-form-item>
        <a-form-item>
          <a-button type="primary" @click="handleSearch">查询</a-button>
          <a-button style="margin-left: 8px" @click="handleReset">重置</a-button>
        </a-form-item>
      </a-form>

      <!-- 表格 -->
      <a-table
        :columns="columns"
        :data-source="list"
        :loading="loading"
        :pagination="pagination"
        row-key="id"
        @change="handleTableChange"
      >
        <template #bodyCell="{ column, record }">
          <template v-if="column.key === 'status'">
            <a-tag :color="record.status === 1 ? 'green' : 'red'">
              {{ record.status === 1 ? '成功' : '失败' }}
            </a-tag>
          </template>
          <template v-else-if="column.key === 'type'">
            <a-tag :color="record.is_test === 1 ? 'orange' : 'blue'">
              {{ record.is_test === 1 ? '测试' : '正式' }}
            </a-tag>
          </template>
          <template v-if="column.key === 'duration_ms'">
            {{ record.duration_ms != null ? record.duration_ms + ' ms' : '-' }}
          </template>
          <template v-else-if="column.key === 'create_time'">
            {{ formatBeijingDateTime(record.create_time) }}
          </template>
        <template v-if="column.key === 'license_plate'">
          {{ formatColumnText(record.license_plate) }}
        </template>
        <template v-if="column.key === 'result_text'">
          <a-tooltip :title="record.result_text || '-'">
            {{ formatColumnText(record.result_text) }}
          </a-tooltip>
        </template>
          <template v-if="column.key === 'action'">
            <a-button type="link" size="small" @click="goDetail(record.id)">
              查看详情
            </a-button>
          </template>
        </template>
      </a-table>
    </a-card>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import dayjs from 'dayjs';
import { getPosttreatmentCheckLogs } from '@/api/posttreatmentCheckLog';
import { formatBeijingDateTime } from '@/utils/dateTime';

const router = useRouter();
const loading = ref(false);
const list = ref([]);

const filters = reactive({
  system_id: '',
  license_plate: '',
  prompt_version: undefined,
  emission_level: undefined,
  pollutant_type: undefined,
  status: undefined,
  timeRange: null
});

const pagination = reactive({
  current: 1,
  pageSize: 20,
  total: 0,
  showSizeChanger: true,
  showTotal: (total) => `共 ${total} 条`
});

const columns = [
  { title: 'ID', dataIndex: 'id', key: 'id', width: 80 },
  { title: '类型', key: 'type', width: 80 },
  { title: '业务标识', dataIndex: 'system_id', key: 'system_id', width: 160, ellipsis: true },
  { title: '车牌号', dataIndex: 'license_plate', key: 'license_plate', width: 160, ellipsis: true },
  { title: '排放阶段', dataIndex: 'emission_level', key: 'emission_level', width: 100 },
  { title: '污染物装置', dataIndex: 'pollutant_type', key: 'pollutant_type', width: 110 },
  { title: '模型', dataIndex: 'model_name', key: 'model_name', width: 160 },
  { title: '结果', dataIndex: 'result_text', key: 'result_text', width: 220, ellipsis: true },
  { title: 'Prompt 长度', dataIndex: 'prompt_length', key: 'prompt_length', width: 110 },
  { title: '耗时', key: 'duration_ms', width: 90 },
  { title: '状态', key: 'status', width: 80 },
  { title: '调用时间', dataIndex: 'create_time', key: 'create_time', width: 170 },
  { title: '操作', key: 'action', width: 90 }
];

function formatColumnText(value) {
  return value || '-';
}

async function fetchData() {
  loading.value = true;
  try {
    const params = {
      page: pagination.current,
      pageSize: pagination.pageSize
    };
    if (filters.system_id) params.system_id = filters.system_id;
    if (filters.license_plate) params.license_plate = filters.license_plate;
    if (Number.isInteger(filters.prompt_version) && filters.prompt_version > 0) {
      params.prompt_version = filters.prompt_version;
    }
    if (filters.emission_level) params.emission_level = filters.emission_level;
    if (filters.pollutant_type) params.pollutant_type = filters.pollutant_type;
    if (filters.status !== undefined && filters.status !== null) params.status = filters.status;
    if (filters.timeRange && filters.timeRange.length === 2) {
      params.start_time = dayjs(filters.timeRange[0]).format('YYYY-MM-DD HH:mm:ss');
      params.end_time = dayjs(filters.timeRange[1]).format('YYYY-MM-DD HH:mm:ss');
    }
    const res = await getPosttreatmentCheckLogs(params);
    if (res.data) {
      list.value = res.data.items || [];
      pagination.total = res.data.total || 0;
    }
  } finally {
    loading.value = false;
  }
}

function handleSearch() {
  pagination.current = 1;
  fetchData();
}

function handleReset() {
  filters.system_id = '';
  filters.license_plate = '';
  filters.prompt_version = undefined;
  filters.emission_level = undefined;
  filters.pollutant_type = undefined;
  filters.status = undefined;
  filters.timeRange = null;
  pagination.current = 1;
  fetchData();
}

function handleTableChange(pag) {
  pagination.current = pag.current;
  pagination.pageSize = pag.pageSize;
  fetchData();
}

function goDetail(id) {
  router.push({ name: 'posttreatment-check-log-detail', params: { id } });
}

onMounted(fetchData);
</script>
